<template>
  <div class="game-purchase-dialogs-host">
    <el-dialog
      :title="$t('gamesPurchase.dialogTitle')"
      :visible.sync="purchaseVisible"
      width="720px"
      top="0"
      append-to-body
      custom-class="zj-game-purchase-dialog zj-game-purchase-dialog--checkout"
      @closed="onPurchaseClosed"
    >
      <div v-if="activeGame" class="gp">
        <div class="gp__summary">
          <div>
            <div class="gp__summaryLabel">{{ isCardGame ? $t('gamesPurchase.cardProduct') : $t('gamesPurchase.licenseProduct') }}</div>
            <div class="gp__name">{{ activeGame.name }}</div>
          </div>
          <div class="gp__priceBlock">
            <span>{{ $t('gamesPurchase.payAmount') }}</span>
            <strong>{{ formatPrice(selectedPurchaseAmount) }}</strong>
          </div>
        </div>
        <el-form label-position="top" class="gp__form">
          <section class="gp__section">
            <div class="gp__sectionHead"><span class="gp__sectionNo">01</span><div><h3>{{ $t('gamesPurchase.authorizationSection') }}</h3><p>{{ $t('gamesPurchase.authorizationSectionDesc') }}</p></div></div>
            <div class="gp__grid">
              <el-form-item :label="$t('gamesPurchase.platform')" required>
                <el-select v-model="selectedPlatform" class="gp__full" :placeholder="$t('gamesPurchase.platformPh')">
                  <el-option v-for="option in platformOptions" :key="option.value" :label="option.label" :value="option.value" />
                </el-select>
              </el-form-item>
              <el-form-item :label="$t('gamesPurchase.authorizationAccount')" required>
                <el-input v-model.trim="authorizationAccount" maxlength="191" clearable :placeholder="$t('gamesPurchase.authorizationAccountPh')" class="gp__full" />
              </el-form-item>
              <el-form-item v-if="!isCardGame" class="gp__gridFull" :label="$t('gamesPurchase.senseyunPhone')" required>
                <el-input v-model.trim="senseyunPhone" maxlength="191" clearable :placeholder="$t('gamesPurchase.senseyunPhonePh')" class="gp__full" />
              </el-form-item>
            </div>
          </section>

          <section v-if="!isCardGame" class="gp__section">
            <div class="gp__sectionHead"><span class="gp__sectionNo">02</span><div><h3>{{ $t('gamesPurchase.planLabel') }}</h3><p>{{ $t('gamesPurchase.planSectionDesc') }}</p></div></div>
            <el-radio-group v-model="licensePlan" class="gp__plan">
              <el-radio label="month">
                <span>{{ $t('gamesPurchase.planMonth') }}</span>
                <strong>{{ formatPrice(monthPrice) }}</strong>
                <small v-if="isAgentUser && Number.isFinite(retailMonthPrice)" class="gp__retailRef">
                  {{ $t('gamesPurchase.retailRef', { price: formatPrice(retailMonthPrice) }) }}
                </small>
              </el-radio>
              <el-radio v-if="hasQuarterPlan" label="quarter">
                <span>{{ $t('gamesPurchase.planQuarter') }}</span>
                <strong>{{ formatPrice(quarterPrice) }}</strong>
                <small v-if="isAgentUser && Number.isFinite(retailQuarterPrice)" class="gp__retailRef">
                  {{ $t('gamesPurchase.retailRef', { price: formatPrice(retailQuarterPrice) }) }}
                </small>
              </el-radio>
              <el-radio v-if="hasYearPlan" label="year">
                <span>{{ $t('gamesPurchase.planYear') }}</span>
                <strong>{{ formatPrice(effectiveYearPrice) }}</strong>
                <small v-if="isAgentUser && Number.isFinite(retailYearPrice)" class="gp__retailRef">
                  {{ $t('gamesPurchase.retailRef', { price: formatPrice(retailYearPrice) }) }}
                </small>
              </el-radio>
            </el-radio-group>
          </section>

          <section v-if="availableCoupons.length" class="gp__section">
            <div class="gp__sectionHead"><span class="gp__sectionNo">{{ isCardGame ? '02' : '03' }}</span><div><h3>{{ $t('coupons.select') }}</h3></div></div>
            <el-select v-model="selectedCouponId" clearable class="gp__full" :placeholder="$t('coupons.selectNone')" @change="onCouponChange">
              <el-option :label="$t('coupons.selectNone')" :value="null" />
              <el-option
                v-for="c in availableCoupons"
                :key="c.id"
                :label="couponOptionLabel(c)"
                :value="c.id"
              />
            </el-select>
            <p v-if="couponDiscount > 0" class="gp__couponTip">{{ $t('coupons.discount', { amount: formatPrice(couponDiscount) }) }} · {{ $t('coupons.payAfter') }} {{ formatPrice(selectedPurchaseAmount) }}</p>
          </section>

          <section class="gp__section gp__section--last">
            <div class="gp__sectionHead"><span class="gp__sectionNo">{{ couponSectionNo }}</span><div><h3>{{ $t('gamesPurchase.paymentSection') }}</h3><p>{{ $t('gamesPurchase.paymentSectionDesc') }}</p></div></div>
            <div class="gp__paymentGrid">
              <button type="button" :class="['gp__payOption', { 'is-active': payType === 'alipay' }]" @click="payType = 'alipay'"><span class="gp__payMark gp__payMark--ali">支</span><span><strong>支付宝</strong><small>ALIPAY</small></span><i class="el-icon-check"></i></button>
              <button type="button" :class="['gp__payOption', { 'is-active': payType === 'wxpay' }]" @click="payType = 'wxpay'"><span class="gp__payMark gp__payMark--wx">微</span><span><strong>微信支付</strong><small>WECHAT PAY</small></span><i class="el-icon-check"></i></button>
            </div>
          </section>
        </el-form>
        <div class="gp__actions">
          <el-button type="primary" plain :loading="balanceLoading" @click="onBalancePay">
            {{ $t('gamesPurchase.balancePay') }}
          </el-button>
          <el-button type="primary" :loading="onlineLoading" @click="onOnlinePay">
            {{ $t('gamesPurchase.submitOnline') }}
          </el-button>
        </div>
      </div>
    </el-dialog>

    <el-dialog
      :visible.sync="qrVisible"
      :title="$t('recharge.scanTitle')"
      width="360px"
      top="0"
      append-to-body
      center
      custom-class="zj-game-purchase-dialog"
    >
      <div class="gp__qr">
        <img v-if="qrDataUrl" class="gp__qrImg" :src="qrDataUrl" alt="qrcode">
        <div class="gp__qrTip">{{ $t('recharge.scanTip') }}</div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="onQrDone">{{ $t('recharge.paidGoResult') }}</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="$t('gamesPurchase.resultTitle')"
      :visible.sync="resultVisible"
      width="440px"
      top="0"
      append-to-body
      custom-class="zj-game-purchase-dialog"
      @closed="onResultClosed"
    >
      <p class="gp__desc">{{ $t('gamesPurchase.resultSub') }}</p>
      <div v-if="resultLoading" class="gp__state">{{ $t('gamesPurchase.polling') }}</div>
      <div v-else-if="resultError" class="gp__state gp__state--err">{{ resultError }}</div>
      <div v-else-if="resultOrder" class="gp__detail">
        <div class="gp__row">
          <span>{{ $t('gamesPurchase.orderNo') }}</span>
          <code>{{ resultOrder.out_trade_no }}</code>
        </div>
        <div class="gp__row">
          <span>{{ $t('recharge.amount') }}</span>
          <strong>{{ formatPrice(resultOrder.amount) }}</strong>
        </div>
        <div class="gp__row">
          <span>{{ $t('gamesPurchase.status') }}</span>
          <el-tag :type="statusType(resultOrder.status)" size="small">{{ statusText(resultOrder.status) }}</el-tag>
        </div>
        <div v-if="resultOrder.platform" class="gp__row">
          <span>{{ $t('gamesPurchase.platform') }}</span>
          <span>{{ platformLabel(resultOrder.platform) }}</span>
        </div>
        <div v-if="resultOrder.authorization_account" class="gp__row">
          <span>{{ $t('gamesPurchase.authorizationAccount') }}</span>
          <span class="gp__sense">{{ resultOrder.authorization_account }}</span>
        </div>
        <div v-if="resultOrder.senseyun_user_account" class="gp__row">
          <span>{{ $t('gamesPurchase.senseyunPhone') }}</span>
          <span>{{ resultOrder.senseyun_user_account }}</span>
        </div>
        <div v-if="resultOrder && resultOrder.delivered_card_secret" class="gp__row gp__row--card">
          <span>{{ $t('gamesPurchase.cardSecret') }}</span>
          <code class="gp__cardSecret">{{ resultOrder.delivered_card_secret }}</code>
        </div>
        <div v-if="resultOrder && resultOrder.senseyun_plan && !resultOrder.delivered_card_secret" class="gp__row">
          <span>{{ $t('gamesPurchase.resultPlan') }}</span>
          <span>{{ planLabelText(resultOrder.senseyun_plan) }}</span>
        </div>
        <div v-if="resultOrder.senseyun_desc" class="gp__row">
          <span>{{ $t('gamesPurchase.senseDesc') }}</span>
          <span class="gp__sense">{{ resultOrder.senseyun_desc }}</span>
        </div>
      </div>
      <el-button
        v-if="!resultLoading && resultOrder && (resultOrder.status === 'pending' || resultOrder.status === 'processing')"
        type="primary"
        class="gp__refresh"
        @click="pollOrderOnce(false)"
      >
        {{ $t('gamesPurchase.refresh') }}
      </el-button>
      <el-button class="gp__refresh" plain @click="resultVisible = false">{{ $t('gamesPurchase.close') }}</el-button>
    </el-dialog>

    <PayQrDanmaku :active="qrVisible" />
  </div>
</template>

<script>
import PayQrDanmaku from '@/components/PayQrDanmaku.vue'
import { getAuthHeader, getSession, isLoggedIn, redirectToLogin, refreshProfileFromServer } from '@/utils/auth'

function apiUrl(path) {
  const root = (process.env.VUE_APP_API_BASE || '').replace(/\/$/, '')
  const p = path.startsWith('/') ? path : `/${path}`
  return root ? `${root}${p}` : p
}

export default {
  name: 'GamePurchaseDialogs',
  components: {
    PayQrDanmaku
  },
  data() {
    return {
      purchaseVisible: false,
      activeGame: null,
      selectedPlatform: 'douyin',
      authorizationAccount: '',
      senseyunPhone: '',
      licensePlan: 'month',
      payType: 'alipay',
      balanceLoading: false,
      onlineLoading: false,
      qrVisible: false,
      qrDataUrl: '',
      lastOutTradeNo: '',
      qrPollTimer: null,
      qrPollTries: 0,
      resultVisible: false,
      resultLoading: false,
      resultError: '',
      resultOrder: null,
      resultTimer: null,
      resultTries: 0,
      resultOutNo: '',
      availableCoupons: [],
      selectedCouponId: null,
      couponDiscount: 0,
      couponOriginalAmount: null
    }
  },
  watch: {
    licensePlan() {
      this.loadAvailableCoupons()
    },
    $route: {
      handler(to) {
        const out = String(to.query.game_pay_out || '').trim()
        if (!out) return
        if (to.name !== 'games') return
        const keep = out
        this.stripGamePayQuery()
        this.purchaseVisible = false
        this.qrVisible = false
        this.clearQrPollTimer()
        this.openResultDialog(keep)
      },
      immediate: true
    },
    qrVisible(visible) {
      if (!visible) {
        this.clearQrPollTimer()
      }
    }
  },
  beforeDestroy() {
    this.clearResultTimer()
    this.clearQrPollTimer()
  },
  computed: {
    monthPrice() {
      if (!this.activeGame) return NaN
      return Number(this.activeGame.price)
    },
    quarterPrice() {
      if (!this.activeGame) return NaN
      const q = this.activeGame.priceQuarter != null ? Number(this.activeGame.priceQuarter) : NaN
      if (Number.isFinite(q) && q >= 0) {
        return Math.round(q * 100) / 100
      }
      return NaN
    },
    effectiveYearPrice() {
      if (!this.activeGame) return NaN
      const y = this.activeGame.priceYear != null ? Number(this.activeGame.priceYear) : NaN
      if (Number.isFinite(y) && y >= 0) {
        return Math.round(y * 100) / 100
      }
      return NaN
    },
    hasQuarterPlan() {
      return Number.isFinite(this.quarterPrice)
    },
    hasYearPlan() {
      return Number.isFinite(this.effectiveYearPrice)
    },
    isAgentUser() {
      const s = getSession()
      const level = s && s.agentLevel ? String(s.agentLevel).toLowerCase() : 'normal'
      return level === 'svip' || level === 'ssvip'
    },
    retailMonthPrice() {
      if (!this.activeGame) return NaN
      const r = this.activeGame.retailPrice
      return r != null ? Number(r) : NaN
    },
    retailQuarterPrice() {
      if (!this.activeGame) return NaN
      const r = this.activeGame.retailPriceQuarter
      return r != null ? Number(r) : NaN
    },
    retailYearPrice() {
      if (!this.activeGame) return NaN
      const r = this.activeGame.retailPriceYear
      return r != null ? Number(r) : NaN
    },
    basePurchaseAmount() {
      if (!this.activeGame) return NaN
      if (this.isCardGame) {
        // zhenggu may use plan
        if (this.licensePlan === 'year') return this.effectiveYearPrice
        if (this.licensePlan === 'quarter') return this.quarterPrice
        return this.monthPrice
      }
      if (this.licensePlan === 'year') return this.effectiveYearPrice
      if (this.licensePlan === 'quarter') return this.quarterPrice
      return this.monthPrice
    },
    selectedPurchaseAmount() {
      const base = this.basePurchaseAmount
      if (!Number.isFinite(base)) return NaN
      if (this.couponDiscount > 0) {
        const finalAmt = Math.round((base - this.couponDiscount) * 100) / 100
        return finalAmt >= 0.01 ? finalAmt : 0.01
      }
      return base
    },
    couponSectionNo() {
      if (this.availableCoupons.length) return this.isCardGame ? '03' : '04'
      return this.isCardGame ? '02' : '03'
    },
    isCardGame() {
      if (!this.activeGame) return false
      return String(this.activeGame.fulfillmentType || '').toLowerCase() === 'card_key'
    },
    platformOptions() {
      return [
        { value: 'douyin', label: this.$t('gamesPurchase.platformDouyin') },
        { value: 'kuaishou', label: this.$t('gamesPurchase.platformKuaishou') },
        { value: 'bilibili', label: this.$t('gamesPurchase.platformBilibili') },
        { value: 'tiktok', label: this.$t('gamesPurchase.platformTiktok') },
        { value: 'xiaohongshu', label: this.$t('gamesPurchase.platformXiaohongshu') }
      ]
    }
  },
  methods: {
    /** 由游戏页在通过校验后调用 */
    open(game) {
      this.activeGame = game
      this.selectedPlatform = 'douyin'
      this.authorizationAccount = ''
      this.senseyunPhone = ''
      this.selectedCouponId = null
      this.couponDiscount = 0
      this.availableCoupons = []
      this.licensePlan = 'month'
      this.purchaseVisible = true
      this.$nextTick(() => {
        this.ensureValidLicensePlan()
        this.loadAvailableCoupons()
      })
    },
    couponOptionLabel(c) {
      const name = (c.template && c.template.name) || this.$t('coupons.unnamed')
      const discount =
        c.quote && c.quote.discount != null
          ? c.quote.discount
          : c.template && c.template.discount_value
      return `${name} -¥${Number(discount).toFixed(2)}`
    },
    onCouponChange() {
      const hit = this.availableCoupons.find((c) => c.id === this.selectedCouponId)
      this.couponDiscount = hit && hit.quote ? Number(hit.quote.discount) : 0
    },
    async loadAvailableCoupons() {
      this.availableCoupons = []
      this.selectedCouponId = null
      this.couponDiscount = 0
      if (process.env.VUE_APP_USE_BACKEND !== 'true' || !this.activeGame || !this.activeGame.id) return
      try {
        const plan = this.licensePlan || 'month'
        const qs = new URLSearchParams({
          game_id: String(this.activeGame.id),
          plan: String(plan)
        })
        const res = await fetch(`${apiUrl('/api/marketing/coupons/available')}?${qs}`, {
          headers: { ...getAuthHeader() }
        })
        const data = await res.json().catch(() => ({}))
        if (data.ok && Array.isArray(data.list)) {
          this.availableCoupons = data.list
          this.couponOriginalAmount =
            data.original_amount != null ? Number(data.original_amount) : null
        }
      } catch (e) {
        /* ignore */
      }
    },
    ensureValidLicensePlan() {
      if (this.isCardGame) {
        this.licensePlan = 'month'
        return
      }
      if (this.licensePlan === 'quarter' && !this.hasQuarterPlan) {
        this.licensePlan = 'month'
      }
      if (this.licensePlan === 'year' && !this.hasYearPlan) {
        this.licensePlan = 'month'
      }
    },
    planLabelText(plan) {
      const p = String(plan || '').toLowerCase()
      if (p === 'year') return this.$t('gamesPurchase.planYear')
      if (p === 'quarter') return this.$t('gamesPurchase.planQuarter')
      return this.$t('gamesPurchase.planMonth')
    },
    normalizedAuthorizationAccount() {
      const account = String(this.authorizationAccount || '').trim()
      if (!account || account.length > 191) return ''
      if (/[,，;；\n\r]/.test(account)) return ''
      return account
    },
    normalizedSenseyunPhone() {
      let value = String(this.senseyunPhone || '').trim().replace(/\s+/g, '')
      if (!value || value.length > 191) return ''
      if (value.includes('@')) {
        const email = value.toLowerCase()
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : ''
      }
      if (value.startsWith('+86')) value = value.slice(3)
      else if (/^86\d{11}$/.test(value)) value = value.slice(2)
      const digits = value.replace(/\D/g, '')
      return /^1[3-9]\d{9}$/.test(digits) ? digits : ''
    },
    platformLabel(value) {
      const option = this.platformOptions.find(item => item.value === value)
      return option ? option.label : value
    },
    validatePurchaseIdentity() {
      if (!this.selectedPlatform) {
        this.$message.warning(this.$t('gamesPurchase.platformInvalid'))
        return false
      }
      if (!this.normalizedAuthorizationAccount()) {
        const raw = String(this.authorizationAccount || '').trim()
        if (raw && /[,，;；\n\r]/.test(raw)) {
          this.$message.warning(this.$t('gamesPurchase.authorizationAccountMultiple'))
        } else {
          this.$message.warning(this.$t('gamesPurchase.authorizationAccountInvalid'))
        }
        return false
      }
      if (!this.isCardGame && !this.normalizedSenseyunPhone()) {
        this.$message.warning(this.$t('gamesPurchase.phoneInvalid'))
        return false
      }
      if (!this.isCardGame && !Number.isFinite(this.selectedPurchaseAmount)) {
        this.$message.error(this.$t('gamesPurchase.invalidGamePrice'))
        return false
      }
      if (!this.isCardGame) {
        if (this.licensePlan === 'quarter' && !this.hasQuarterPlan) {
          this.$message.error(this.$t('gamesPurchase.planUnavailable'))
          return false
        }
        if (this.licensePlan === 'year' && !this.hasYearPlan) {
          this.$message.error(this.$t('gamesPurchase.planUnavailable'))
          return false
        }
      }
      return true
    },
    /** 深盾：仅产品许可 ID（issueLicenseByProduct） */
    activeGameHasSenseyunTarget() {
      if (!this.activeGame) return false
      const lid =
        this.activeGame.senseyunLicenseId != null ? Number(this.activeGame.senseyunLicenseId) : NaN
      return Number.isFinite(lid) && lid > 0
    },
    buildPurchasePayload() {
      const gid = this.activeGame && this.activeGame.id != null ? Number(this.activeGame.id) : 0
      if (this.isCardGame) {
        return {
          gameId: gid,
          licenseId: 0,
          plan: 'month',
          platform: this.selectedPlatform,
          authorizationAccount: this.normalizedAuthorizationAccount(),
          user_coupon_id: this.selectedCouponId || undefined
        }
      }
      const lid =
        this.activeGame.senseyunLicenseId != null ? Number(this.activeGame.senseyunLicenseId) : NaN
      const licenseId = Number.isFinite(lid) && lid > 0 ? Math.trunc(lid) : 0
      return {
        gameId: gid,
        licenseId,
        plan: this.licensePlan,
        platform: this.selectedPlatform,
        authorizationAccount: this.normalizedAuthorizationAccount(),
        senseyunPhone: this.normalizedSenseyunPhone(),
        user_coupon_id: this.selectedCouponId || undefined
      }
    },
    formatPrice(value) {
      const amount = Number(value)
      if (Number.isNaN(amount)) return '¥0.00'
      return new Intl.NumberFormat('zh-CN', {
        style: 'currency',
        currency: 'CNY'
      }).format(amount)
    },
    stripGamePayQuery() {
      const q = { ...this.$route.query }
      if (!q.game_pay_out) return
      delete q.game_pay_out
      this.$router.replace({ path: this.$route.path, query: q })
    },
    clearResultTimer() {
      if (this.resultTimer) {
        clearInterval(this.resultTimer)
        this.resultTimer = null
      }
    },
    clearQrPollTimer() {
      if (this.qrPollTimer) {
        clearInterval(this.qrPollTimer)
        this.qrPollTimer = null
      }
      this.qrPollTries = 0
    },
    onPurchaseClosed() {
      this.balanceLoading = false
      this.onlineLoading = false
      this.activeGame = null
      this.selectedPlatform = 'douyin'
      this.authorizationAccount = ''
      this.senseyunPhone = ''
      this.licensePlan = 'month'
    },
    /**
     * 扫码支付中自动轮询；成功后关闭购买窗/二维码窗并打开结果
     */
    startQrPaymentPolling(outTradeNo) {
      const out = String(outTradeNo || '').trim()
      if (!out) return
      this.clearQrPollTimer()
      this.qrPollTries = 0
      this.pollQrPaymentOnce(out)
      this.qrPollTimer = setInterval(() => {
        this.qrPollTries += 1
        if (this.qrPollTries > 60) {
          this.clearQrPollTimer()
          return
        }
        this.pollQrPaymentOnce(out)
      }, 2000)
    },
    async pollQrPaymentOnce(outTradeNo) {
      const out = String(outTradeNo || this.lastOutTradeNo || '').trim()
      if (!out || !this.qrVisible) return
      const headers = { ...getAuthHeader() }
      if (!headers.Authorization) return
      try {
        const res = await fetch(
          `${apiUrl('/api/games/purchase/status')}?out_trade_no=${encodeURIComponent(out)}`,
          { headers }
        )
        const data = await res.json().catch(() => ({}))
        if (!data.ok || !data.order) return
        const status = String(data.order.status || '')
        if (status === 'paid' || status === 'license_failed') {
          this.clearQrPollTimer()
          this.qrVisible = false
          this.purchaseVisible = false
          if (status === 'paid') {
            this.$message.success(this.$t('gamesPurchase.successBalance'))
            await refreshProfileFromServer()
          }
          this.openResultDialog(out)
        }
      } catch (e) {
        /* 轮询失败时静默，等待下次 */
      }
    },
    statusType(s) {
      if (s === 'paid') return 'success'
      if (s === 'failed' || s === 'license_failed') return 'danger'
      return 'info'
    },
    statusText(s) {
      const map = {
        pending: this.$t('gamesPurchase.stPending'),
        paid: this.$t('gamesPurchase.stPaid'),
        failed: this.$t('gamesPurchase.stFailed'),
        processing: this.$t('gamesPurchase.stProcessing'),
        license_failed: this.$t('gamesPurchase.stLicenseFailed'),
        closed: this.$t('gamesPurchase.stClosed')
      }
      return map[s] || s
    },
    openResultDialog(outTradeNo) {
      this.resultOutNo = outTradeNo
      this.resultVisible = true
      this.resultLoading = true
      this.resultError = ''
      this.resultOrder = null
      this.resultTries = 0
      this.clearResultTimer()
      this.pollOrderOnce(false)
      this.resultTimer = setInterval(() => {
        this.resultTries += 1
        if (this.resultTries > 40) {
          this.clearResultTimer()
          return
        }
        if (this.resultOrder && (this.resultOrder.status === 'paid' || this.resultOrder.status === 'license_failed')) {
          this.clearResultTimer()
        }
        this.pollOrderOnce(true)
      }, 2500)
    },
    onResultClosed() {
      this.clearResultTimer()
      this.resultOrder = null
      this.resultError = ''
      this.resultOutNo = ''
    },
    async pollOrderOnce(silent) {
      const out = String(this.resultOutNo || '').trim()
      if (!out) {
        this.resultError = this.$t('recharge.missingOrder')
        this.resultLoading = false
        return
      }
      const headers = { ...getAuthHeader() }
      if (!headers.Authorization) {
        this.resultError = this.$t('gamesPurchase.needLogin')
        this.resultLoading = false
        return
      }
      if (!silent) this.resultLoading = true
      try {
        const res = await fetch(
          `${apiUrl('/api/games/purchase/status')}?out_trade_no=${encodeURIComponent(out)}`,
          { headers }
        )
        const data = await res.json().catch(() => ({}))
        if (!data.ok) {
          this.resultError = data.message || this.$t('recharge.queryFailed')
          this.resultOrder = null
          return
        }
        this.resultOrder = data.order
        this.resultError = ''
        if (data.order && (data.order.status === 'paid' || data.order.status === 'license_failed')) {
          this.clearResultTimer()
          this.purchaseVisible = false
          this.qrVisible = false
          if (data.order.status === 'paid') {
            await refreshProfileFromServer()
          }
        }
      } catch (e) {
        this.resultError = this.$t('recharge.network')
      } finally {
        if (!silent) this.resultLoading = false
      }
    },
    onQrDone() {
      this.clearQrPollTimer()
      this.qrVisible = false
      if (this.lastOutTradeNo) {
        this.openResultDialog(this.lastOutTradeNo)
      }
    },
    async onBalancePay() {
      if (!this.activeGame) return
      if (!this.validatePurchaseIdentity()) return
      if (!this.isCardGame) {
        if (!this.activeGameHasSenseyunTarget()) {
          this.$message.error(this.$t('gamesPurchase.licenseMissing'))
          return
        }
      }
      if (!isLoggedIn()) {
        this.$message.error(this.$t('gamesPurchase.needLogin'))
        this.purchaseVisible = false
        redirectToLogin(this.$router, this.$route.fullPath)
        return
      }
      const headers = {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
      this.balanceLoading = true
      try {
        const res = await fetch(apiUrl('/api/games/purchase/balance'), {
          method: 'POST',
          headers,
          body: JSON.stringify(this.buildPurchasePayload())
        })
        const data = await res.json().catch(() => ({}))
        if (!data.ok) {
          if (data.message === 'INVALID_PLATFORM') {
            this.$message.warning(this.$t('gamesPurchase.platformInvalid'))
          } else if (data.message === 'INVALID_AUTHORIZATION_ACCOUNT') {
            this.$message.warning(this.$t('gamesPurchase.authorizationAccountInvalid'))
          } else if (data.message === 'GAME_NOT_FOUND') {
            this.$message.error(this.$t('gamesPurchase.gameNotFound'))
          } else if (data.message === 'GAME_OFF_SHELF') {
            this.$message.error(this.$t('gamesPurchase.gameOffShelf'))
          } else if (data.message === 'OUT_OF_STOCK') {
            this.$message.error(this.$t('gamesPurchase.outOfStock'))
          } else if (data.message === 'INSUFFICIENT_BALANCE') {
            this.$message.error(this.$t('gamesPurchase.insufficient'))
          } else if (data.message === 'GAME_LICENSE_NOT_SET') {
            this.$message.error(this.$t('gamesPurchase.licenseMissing'))
          } else if (data.message === 'INVALID_GAME_PRICE') {
            this.$message.error(this.$t('gamesPurchase.invalidGamePrice'))
          } else if (data.message === 'SENSEYUN_NOT_CONFIGURED') {
            this.$message.error(this.$t('gamesPurchase.senseyunError'))
          } else if (data.message === 'SENSEYUN_LICENSE_NOT_FOUND') {
            this.$message.error(this.$t('gamesPurchase.senseyunLicenseNotFound'))
          } else if (data.message === 'SENSEYUN_USER_NOT_FOUND') {
            this.$message.error(this.$t('gamesPurchase.senseyunUserNotFound'))
          } else if (data.message === 'SENSEYUN_ISSUE_FAILED') {
            this.$message.error((data.senseyun && data.senseyun.desc) || this.$t('gamesPurchase.senseyunError'))
          } else if (data.message === 'DEBIT_AFTER_LICENSE_FAILED') {
            this.$message.error(this.$t('gamesPurchase.debitHint'))
          } else {
            this.$message.error(data.message || this.$t('gamesPurchase.senseyunError'))
          }
          return
        }
        this.$message.success(this.$t('gamesPurchase.successBalance'))
        this.purchaseVisible = false
        await refreshProfileFromServer()
        const out = String(data.out_trade_no || '').trim()
        if (out) {
          this.lastOutTradeNo = out
          this.openResultDialog(out)
        }
      } catch (e) {
        this.$message.error(this.$t('recharge.network'))
      } finally {
        this.balanceLoading = false
      }
    },
    async onOnlinePay() {
      if (!this.activeGame) return
      if (!this.validatePurchaseIdentity()) return
      if (!this.isCardGame) {
        if (!this.activeGameHasSenseyunTarget()) {
          this.$message.error(this.$t('gamesPurchase.licenseMissing'))
          return
        }
      }
      if (!isLoggedIn()) {
        this.$message.error(this.$t('gamesPurchase.needLogin'))
        this.purchaseVisible = false
        redirectToLogin(this.$router, this.$route.fullPath)
        return
      }
      const headers = {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
      this.onlineLoading = true
      this.qrVisible = false
      try {
        const res = await fetch(apiUrl('/api/games/purchase/pay-create'), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            ...this.buildPurchasePayload(),
            type: this.payType,
            method: 'web'
          })
        })
        const data = await res.json().catch(() => ({}))
        if (!data.ok) {
          if (data.message === 'INVALID_PLATFORM') {
            this.$message.warning(this.$t('gamesPurchase.platformInvalid'))
          } else if (data.message === 'INVALID_AUTHORIZATION_ACCOUNT') {
            this.$message.warning(this.$t('gamesPurchase.authorizationAccountInvalid'))
          } else if (data.message === 'GAME_NOT_FOUND') {
            this.$message.error(this.$t('gamesPurchase.gameNotFound'))
          } else if (data.message === 'GAME_OFF_SHELF') {
            this.$message.error(this.$t('gamesPurchase.gameOffShelf'))
          } else if (data.message === 'OUT_OF_STOCK') {
            this.$message.error(this.$t('gamesPurchase.outOfStock'))
          } else if (data.message === 'GAME_LICENSE_NOT_SET') {
            this.$message.error(this.$t('gamesPurchase.licenseMissing'))
          } else if (data.message === 'INVALID_GAME_PRICE') {
            this.$message.error(this.$t('gamesPurchase.invalidGamePrice'))
          } else if (data.message === 'SENSEYUN_NOT_CONFIGURED') {
            this.$message.error(this.$t('gamesPurchase.senseyunError'))
          } else if (data.message === 'PAY_PUBLIC_API_BASE_NOT_SET' || data.message === 'PAY_RETURN_FRONT_BASE_NOT_SET') {
            this.$message.error(this.$t('gamesPurchase.configMissing'))
          } else if (res.status === 401) {
            this.$message.error(this.$t('gamesPurchase.needLogin'))
            this.purchaseVisible = false
            redirectToLogin(this.$router, this.$route.fullPath)
          } else {
            this.$message.error(data.detail || data.message || this.$t('recharge.failed'))
          }
          return
        }
        this.lastOutTradeNo = data.out_trade_no
        const payType = String(data.pay_type || '')
        const payInfo = String(data.pay_info || '')
        if (payType === 'jump') {
          this.$message.info(this.$t('gamesPurchase.jumpTip'))
          window.location.href = payInfo
          return
        }
        if (payType === 'qrcode') {
          this.qrDataUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(payInfo)}`
          this.qrVisible = true
          this.startQrPaymentPolling(data.out_trade_no)
          return
        }
        this.$message.info(payInfo || this.$t('recharge.payInfo'))
      } catch (e) {
        this.$message.error(this.$t('recharge.network'))
      } finally {
        this.onlineLoading = false
      }
    }
  }
}
</script>

<style scoped>
.game-purchase-dialogs-host {
  display: none;
}

.gp {
  min-width: 0;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

.gp__summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 18px 20px;
  border: 1px solid #e7e9eb;
  border-radius: 14px;
  background: linear-gradient(135deg, #f2f7f8, #ffffff);
}

.gp__summaryLabel {
  margin-bottom: 5px;
  color: #7b9197;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .12em;
}

.gp__name {
  font-size: 19px;
  font-weight: 800;
  color: #202327;
}

.gp__priceBlock {
  flex-shrink: 0;
  text-align: right;
}

.gp__priceBlock span {
  display: block;
  margin-bottom: 3px;
  color: #82969b;
  font-size: 10px;
}

.gp__priceBlock strong {
  color: #202327;
  font-size: 24px;
  letter-spacing: -.03em;
}

.gp__form {
  margin-top: 20px;
  min-width: 0;
}

.gp__full {
  width: 100%;
}
.gp__couponTip {
  margin: 8px 0 0;
  color: #c2410c;
  font-size: 13px;
}

.gp__plan {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px;
  width: 100%;
  max-width: 100%;
}

.gp__plan :deep(.el-radio) {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  margin: 0;
  padding: 12px 14px;
  border: 1px solid #e7e9eb;
  border-radius: 9px;
  background: #fff;
}

.gp__plan :deep(.el-radio__label) {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px 8px;
  min-width: 0;
  max-width: 100%;
  width: auto;
  padding-left: 8px;
  white-space: normal;
  word-break: break-word;
}

.gp__retailRef {
  flex-basis: 100%;
  margin-left: 0;
  color: #8a9096;
  font-size: 12px;
  font-weight: normal;
}

.gp__plan :deep(.el-radio__label strong) {
  color: #202327;
  font-size: 14px;
}

.gp__plan :deep(.el-radio__input.is-checked .el-radio__inner) {
  border-color: #2f6bff;
  background: #2f6bff;
}

.gp__plan :deep(.el-radio__input.is-checked + .el-radio__label) {
  color: #1f4fff;
}

.gp__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid #eceef0;
}

.gp__section {
  padding: 0 0 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #eceef0;
}

.gp__section--last {
  padding-bottom: 0;
  margin-bottom: 0;
  border-bottom: 0;
}

.gp__sectionHead {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  margin-bottom: 15px;
}

.gp__sectionNo {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #f3f4f5;
  color: #4d555d;
  font-size: 10px;
  font-weight: 800;
}

.gp__sectionHead h3 {
  margin: 1px 0 3px;
  color: #202327;
  font-size: 14px;
}

.gp__sectionHead p {
  margin: 0;
  color: #899b9f;
  font-size: 11px;
}

.gp__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 14px;
}

.gp__gridFull {
  grid-column: 1 / -1;
}

.gp__paymentGrid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.gp__grid > *,
.gp__plan > *,
.gp__paymentGrid > * {
  min-width: 0;
}

.gp__payOption {
  display: flex;
  align-items: center;
  gap: 11px;
  position: relative;
  padding: 12px 14px;
  border: 1px solid #e7e9eb;
  border-radius: 10px;
  background: #fff;
  color: #526b72;
  text-align: left;
  cursor: pointer;
  min-width: 0;
  box-sizing: border-box;
}

.gp__payOption > span:nth-child(2) {
  min-width: 0;
}

.gp__payOption.is-active {
  border-color: #2f6bff;
  box-shadow: 0 0 0 3px rgba(47, 107, 255, .08);
}

.gp__payMark {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  color: #fff;
  font-size: 13px;
  font-weight: 800;
}

.gp__payMark--ali { background: #1677ff; }
.gp__payMark--wx { background: #07c160; }
.gp__payOption strong { display: block; color: #202327; font-size: 13px; }
.gp__payOption small { display: block; margin-top: 2px; color: #95a4a8; font-size: 8px; letter-spacing: .08em; }
.gp__payOption > i { position: absolute; top: 9px; right: 9px; color: #2f6bff; opacity: 0; }
.gp__payOption.is-active > i { opacity: 1; }

.gp__actions :deep(.el-button) {
  flex: 1;
  min-width: 150px;
  margin: 0;
}

.gp__qr {
  text-align: center;
}

.gp__qrImg {
  width: 220px;
  height: 220px;
  display: inline-block;
}

.gp__qrTip {
  margin-top: 10px;
  color: rgba(15, 23, 42, 0.62);
  font-size: 13px;
}

.gp__state {
  padding: 10px 0;
  color: rgba(15, 23, 42, 0.72);
}

.gp__state--err {
  color: #b91c1c;
}

.gp__detail {
  margin-top: 8px;
}

.gp__row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #eceef0;
  font-size: 13px;
  color: #60777d;
}

.gp__row code {
  font-size: 12px;
  word-break: break-all;
  text-align: right;
}

.gp__sense {
  max-width: 62%;
  text-align: right;
  word-break: break-word;
}

.gp__row--card {
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}

.gp__cardSecret {
  display: block;
  text-align: left;
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 13px;
  padding: 8px;
  border: 1px solid #e7e9eb;
  background: #f3f4f5;
  border-radius: 9px;
}

.gp__refresh {
  margin-top: 12px;
  width: 100%;
}

@media (max-width: 760px) {
  .gp__summary { padding: 15px; }
  .gp__grid,
  .gp__paymentGrid { grid-template-columns: 1fr; }
  .gp__gridFull { grid-column: auto; }
  .gp__actions { flex-direction: column; }
  .gp__actions :deep(.el-button) { width: 100%; }
}
</style>

